import React, { useEffect, useState, useCallback } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { SubscriptionTransferRequestService } from "../../services/subscriptionTransferService";
import {
  SubscriptionTransferRequest,
  SubscriptionTransferStep,
  mergeTransferOutputWithDefaults,
  visibleTransferOrchestrationStepKeys,
  type RevenueQcGroupVerdictStatus,
  type RevenueQcVerdictGroup,
} from "models/subscription-transfer";
import {
  Box,
  Typography,
  Paper,
  CircularProgress,
  Alert,
  AlertTitle,
  Link,
  Chip,
  IconButton,
} from "@mui/material";
import ArrowBackIcon from "@mui/icons-material/ArrowBack";
import OpenInNewIcon from "@mui/icons-material/OpenInNew";
import InputDetails from "./display-components/InputDetails";
import { CommonStep } from "./display-components/SubscriptionTransferOutputSteps";
import StatusChip from "../general/StatusChip";
import { generateNetsuiteLink } from "utils/netsuite";
import { isProdHost } from "../../util/helpers";

const isProd = () => isProdHost();

const NETSUITE_BASE_SUBDOMAIN = () => (isProd() ? "4914352" : "4914352-sb2");

const netsuiteUrl = (path: string): string =>
  `${generateNetsuiteLink(NETSUITE_BASE_SUBDOMAIN(), "base", "", "")}${path}`;

const formatRevenueQcGroupStatus = (status: RevenueQcGroupVerdictStatus): string => {
  switch (status) {
    case "PASS":
      return "PASSED";
    case "FAIL":
      return "FAILED";
    case "SKIPPED":
      return "SKIPPED";
    case "NOT_RUN":
      return "NOT RUN";
    default:
      return status;
  }
};

const revenueQcGroupChipColor = (
  status: RevenueQcGroupVerdictStatus,
): "success" | "error" | "default" => {
  if (status === "PASS") {
    return "success";
  }
  if (status === "FAIL") {
    return "error";
  }
  return "default";
};

/** Deep-link to the QC tab when gid is embedded in the URL or stored separately. */
const buildQcReportHref = (
  qcReportUrl: string | undefined,
  qcReportSheetGid: number | undefined,
): string | undefined => {
  if (!qcReportUrl) {
    return undefined;
  }
  if (/#gid=\d+/.test(qcReportUrl)) {
    return qcReportUrl;
  }
  if (qcReportSheetGid != null) {
    const base = qcReportUrl.split("#")[0]?.replace(/\?$/, "") ?? qcReportUrl;
    return `${base}#gid=${qcReportSheetGid}`;
  }
  return qcReportUrl;
};

/**
 * Steps that the orchestrator deliberately skips on a same-day transfer
 * (cutover === earliest READY service-start date). When `SKIPPED` shows up
 * on one of these steps and the transfer is same-day, the UI treats it as
 * an intentional no-op rather than a warning so the operator isn't
 * misled into thinking something went wrong.
 */
const SAME_DAY_INTENTIONAL_SKIP_STEPS: ReadonlySet<string> = new Set([
  "SubscriptionLinesAndPriceIntervals",
  "ChangeCustomerPreferences",
  "VerifyCmInvoiceAmountParity",
  "RevertPreferences",
  "CreateCreditMemo",
  "RenameCreditMemoToInternalOnly",
  "CreateNewInvoice",
  "ApplyCustomerPayment",
  "ReconcileCreditMemoToInvoice",
  "CreateJournalEntry",
]);

const SAME_DAY_SKIP_DETAIL = "Same-day transfer — not applicable";

/**
 * Render a monetary amount in the transfer's currency. Uses
 * `Intl.NumberFormat` when an ISO 4217 code is available (backend captures
 * it once per transfer from the old subscription's currency.refName), so
 * USD shows as "$3,008.08", EUR as "€586.67", etc. Falls back to a plain
 * thousand-separated number when no currency code is persisted (older
 * transfers from before the field existed, or a currency NetSuite uses but
 * we haven't mapped yet).
 */
const formatAmount = (
  value: number | null | undefined,
  currencyCode: string | null | undefined,
): string | null => {
  if (value == null || !Number.isFinite(Number(value))) return null;
  const numeric = Number(value);
  const code = (currencyCode || "").trim().toUpperCase();
  if (code) {
    try {
      return new Intl.NumberFormat(undefined, {
        style: "currency",
        currency: code,
      }).format(numeric);
    } catch {
      // Invalid currency code — fall through to bare numeric formatting.
    }
  }
  return new Intl.NumberFormat(undefined, {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  }).format(numeric);
};

const SubscriptionTransfer: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [subTransferRequest, setSubTransferRequest] =
    useState<SubscriptionTransferRequest | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [refreshing, setRefreshing] = useState(false);

  const fetchSubTransferRequest = useCallback(async () => {
    const lookupId = id?.split("-")?.[1];
    if (lookupId) {
      const subscriptionTransferService = new SubscriptionTransferRequestService();
      setRefreshing(true); // Set refreshing to true before fetching data
      const startTime = Date.now(); // Record the start time
      try {
        const subTransferData = await subscriptionTransferService.getSubscriptionTransferRequestById(lookupId);
        subTransferData.output = mergeTransferOutputWithDefaults(subTransferData.output);
        console.log("Subscription Transfer Request:", subTransferData);
        setSubTransferRequest(subTransferData);
      } catch (err) {
        setError("Failed to fetch subscription transfer data.");
      }
      const elapsed = Date.now() - startTime;
      const delay = Math.max(0, 2000 - elapsed); // Calculate delay to ensure 2 seconds
      setTimeout(() => {
          setLoading(false);
          setRefreshing(false); // Set refreshing to false after fetching data
        },
       delay
      );
    }
  }, [id]);

  useEffect(() => {
    fetchSubTransferRequest();
  }, [fetchSubTransferRequest]);

  useEffect(() => {
    if (subTransferRequest?.status === "IN_PROGRESS") {
      const intervalId = setInterval(() => {
        fetchSubTransferRequest();
      }, 5000); // Change the interval to 5 seconds
      return () => clearInterval(intervalId);
    }
  }, [subTransferRequest, fetchSubTransferRequest]);

  if (loading) {
    return (
      <Box
        display="flex"
        justifyContent="center"
        alignItems="center"
        height="100vh"
      >
        <CircularProgress />
      </Box>
    );
  }

  if (error) {
    return <Alert severity="error">{error}</Alert>;
  }

  const transferDateMode = subTransferRequest?.output.billingBoundaryContext?.transferDateMode;
  // ISO 4217 currency code captured once at transfer start by the orchestrator
  // (from the old subscription's currency.refName). Drives `formatAmount`
  // below so invoice / payment / CM-adjustment numbers render with the
  // correct symbol and locale separators.
  const currencyCode = subTransferRequest?.output.currencyCode;

  const renderOutputStep = (key: string, displayedSeq: number) => {
    let step: any = null;
    let displayId: string | undefined = undefined;
    let displayUrl: string | undefined = undefined;
    let errorMessage: string | undefined = undefined;
    let detail: string | undefined = undefined;
    switch (key) {
      case "LinkCustomerToTargetSubsidiary": {
        step = subTransferRequest!.output.LinkCustomerToTargetSubsidiary;
        break;
      }
      case "CreateBillingAccount": {
        step = subTransferRequest!.output.CreateBillingAccount;
        displayId = step.billingAccountId
          ? `Billing Account ID: ${step.billingAccountId}`
          : undefined;
        displayUrl = step.billingAccountId
          ? `/accounting/otherlists/billingaccount.nl?id=${step.billingAccountId}`
          : undefined;
        break;
      }
      case "CreateNewSubscription": {
        step = subTransferRequest!.output.CreateNewSubscription;
        displayId = step.newSubscriptionId
          ? `New Subscription ID: ${step.newSubscriptionId}`
          : undefined;
        displayUrl = step.newSubscriptionId
          ? `/accounting/subscription/subscription.nl?id=${step.newSubscriptionId}`
          : undefined;
        const subCount = (step.employeeSubstitutions ?? []).length;
        if (subCount > 0) {
          detail = `Note: ${subCount} employee field${subCount === 1 ? "" : "s"} needed remediation (see banner above).`;
        }
        break;
      }
      case "SyncBothSubscriptions": {
        step = subTransferRequest!.output.SyncBothSubscriptions;
        break;
      }
      case "SubscriptionLinesAndPriceIntervals": {
        step = subTransferRequest!.output.SubscriptionLinesAndPriceIntervals;
        break;
      }
      case "TerminateSubsciption": {
        step = subTransferRequest!.output.TerminateSubsciption;
        displayId = step.subscriptionId
          ? `Old Subscription ID: ${step.subscriptionId}`
          : undefined;
        displayUrl = step.subscriptionId
          ? `/accounting/subscription/subscription.nl?id=${step.subscriptionId}`
          : undefined;
        break;
      }
      case "ChangeCustomerPreferences": {
        step = subTransferRequest!.output.ChangeCustomerPreferences;
        break;
      }
      case "VerifyCmInvoiceAmountParity": {
        step = subTransferRequest!.output.VerifyCmInvoiceAmountParity;
        if (step?.status === "COMPLETED" || step?.status === "FAILED") {
          const expCm = formatAmount(step.compareCmAbs, currencyCode);
          const expInv = formatAmount(step.expectedInvoiceSum, currencyCode);
          const gap = formatAmount(step.parityGap, currencyCode);
          const tol = step.tolerance != null ? `$${Number(step.tolerance).toFixed(2)}` : "$0.01";
          if (expCm && expInv) {
            detail =
              `Expected |CM| ${expCm} vs invoice ${expInv}` +
              (gap ? ` (gap ${gap}, tolerance ${tol})` : "") +
              (step.verificationMode ? ` · ${step.verificationMode}` : "");
          }
        }
        errorMessage = step.errorMessage;
        break;
      }
      case "CreateCreditMemo": {
        step = subTransferRequest!.output.CreateCreditMemo;
        displayId = step.creditMemoId
          ? `Credit Memo ID: ${step.creditMemoId}`
          : undefined;
        displayUrl = step.creditMemoId
          ? `/accounting/transactions/custcred.nl?id=${step.creditMemoId}`
          : undefined;
        break;
      }
      case "RenameCreditMemoToInternalOnly": {
        step = subTransferRequest!.output.RenameCreditMemoToInternalOnly;
        break;
      }
      case "RevertPreferences": {
        step = subTransferRequest!.output.RevertPreferences;
        break;
      }
      case "CreateNewInvoice": {
        step = subTransferRequest!.output.CreateNewInvoice;
        displayId = step.invoiceId
          ? `Invoice ID: ${step.invoiceId}`
          : undefined;
        displayUrl = step.invoiceId
          ? `/accounting/transactions/custinvc.nl?id=${step.invoiceId}`
          : undefined;
        if (step.invoiceTotal != null) {
          const formatted = formatAmount(step.invoiceTotal, currencyCode);
          if (formatted) {
            detail = `Invoice total: ${formatted}`;
          }
        }
        break;
      }
      case "ApplyCustomerPayment": {
        step = subTransferRequest!.output.ApplyCustomerPayment;
        displayId = step.paymentId
          ? `Customer Payment ID: ${step.paymentId}`
          : undefined;
        displayUrl = step.paymentId
          ? `/accounting/transactions/custpymt.nl?id=${step.paymentId}`
          : undefined;
        // Surface the most specific failure detail for split steps: the apply
        // error (Phase 2) takes precedence over the create error (Phase 1)
        // since apply runs after create succeeds.
        errorMessage =
          step.paymentApplyError ||
          step.paymentCreateError ||
          step.errorMessage;
        const invoiceTotal = subTransferRequest!.output.CreateNewInvoice.invoiceTotal;
        if (invoiceTotal != null && step.status === "COMPLETED") {
          const formatted = formatAmount(invoiceTotal, currencyCode);
          if (formatted) {
            detail = `Applied to invoice: ${formatted}`;
          }
        }
        break;
      }
      case "ReconcileCreditMemoToInvoice": {
        step = subTransferRequest!.output.ReconcileCreditMemoToInvoice;
        // Reconcile mutates an existing credit memo rather than creating a
        // new record. Show the outcome inline so the user can see whether
        // any adjustment was needed, without having to click into the CM
        // (which is already linked from the CreateCreditMemo card above).
        if (step.didMutateCreditMemo) {
          const adj = formatAmount(step.adjustmentApplied, currencyCode);
          const before = formatAmount(step.creditMemoTotalBefore, currencyCode);
          const after = formatAmount(step.creditMemoTotalAfter, currencyCode);
          detail = adj != null && before != null && after != null
            ? `Adjusted credit memo by ${adj} (${before} \u2192 ${after})`
            : `Credit memo adjusted to match invoice total`;
        } else if (step.didMutateCreditMemo === false) {
          detail = `No adjustment needed — credit memo total already matched invoice`;
        }
        break;
      }
      case "CreateJournalEntry": {
        step = subTransferRequest!.output.CreateJournalEntry;
        displayId = step.journalId
          ? `Journal ID: ${step.journalId}`
          : undefined;
        displayUrl = step.journalId
          ? `/accounting/transactions/icjournal.nl?id=${step.journalId}`
          : undefined;
        // For split steps, surface the most specific failure detail.
        errorMessage =
          step.aijeApplyError ||
          step.aijeCreateError ||
          step.errorMessage;
        break;
      }
      case "TurnOfAutoRenewal": {
        step = subTransferRequest!.output.TurnOfAutoRenewal;
        break;
      }
      case "DeleteDraftSubscription": {
        step = subTransferRequest!.output.DeleteDraftSubscription;
        break;
      }
      default:
        return null;
    }
    if (!step) {
      return null;
    }
    if (!detail && step.status === "SKIPPED" && step.skipReason === "SAME_DAY_TRANSFER") {
      detail = SAME_DAY_SKIP_DETAIL;
    }
    const intentionalSkip =
      step.status === "SKIPPED" &&
      (step.skipReason === "SAME_DAY_TRANSFER" ||
        (transferDateMode === "SAME_DAY" && SAME_DAY_INTENTIONAL_SKIP_STEPS.has(key)));
    return (
      <CommonStep
        name={step.name}
        status={step.status}
        step={displayedSeq}
        displayId={displayId}
        displayUrl={displayUrl}
        detail={detail}
        intentionalSkip={intentionalSkip}
        errorMessage={
          step.status === "FAILED" || step.status === "IN_PROGRESS"
            ? errorMessage ?? (step as SubscriptionTransferStep).errorMessage
            : undefined
        }
      />
    );
  };

  // Step execution order — shared with backend via models (includes VerifyCmInvoiceAmountParity).
  const visibleStepKeys = subTransferRequest
    ? visibleTransferOrchestrationStepKeys(
        subTransferRequest.output,
        subTransferRequest.status,
      )
    : [];

  const output = subTransferRequest?.output;
  const input = subTransferRequest?.input;
  const cutoverDate = output?.cutoverDate ?? input?.startDate;
  const isFutureDated =
    cutoverDate != null && cutoverDate > new Date().toISOString().split("T")[0];
  const employeeSubstitutions = output?.CreateNewSubscription?.employeeSubstitutions ?? [];
  const newSubscriptionId = output?.CreateNewSubscription?.newSubscriptionId;
  const oldSubscriptionId = input?.subscriptionId;
  const qcReportUrl = output?.qcReportUrl;
  const qcReportSheetName = output?.qcReportSheetName;
  const qcReportHref = buildQcReportHref(
    qcReportUrl,
    output?.qcReportSheetGid,
  );
  const revenueQcReportHref = buildQcReportHref(
    output?.revenueQcReportUrl,
    output?.revenueQcReportSheetGid,
  );
  const revenueQcVerdict = output?.revenueQcVerdict;
  const transferIdUrl = newSubscriptionId
    ? netsuiteUrl(`/accounting/subscription/subscription.nl?id=${newSubscriptionId}`)
    : undefined;
  const oldSubscriptionUrl = oldSubscriptionId
    ? netsuiteUrl(`/accounting/subscription/subscription.nl?id=${oldSubscriptionId}`)
    : undefined;

  const transferProfile = output?.transferProfileContext;
  const draftSubscriptionGate = output?.draftSubscriptionGate;
  const cmInvoiceParityGate =
    output?.VerifyCmInvoiceAmountParity?.status === "FAILED"
      ? {
          summary: output?.cmInvoicePreCreateParityGate?.summary,
          compareCmAbs: output?.VerifyCmInvoiceAmountParity?.compareCmAbs,
          expectedInvoiceSum: output?.VerifyCmInvoiceAmountParity?.expectedInvoiceSum,
          parityGap: output?.VerifyCmInvoiceAmountParity?.parityGap,
          tolerance: output?.VerifyCmInvoiceAmountParity?.tolerance,
          phase:
            output?.VerifyCmInvoiceAmountParity?.verificationMode === "CM_TOTAL_VS_INVOICE_POOL"
              ? "BEFORE_INVOICE"
              : "BEFORE_CREDIT_MEMO",
          recommendations:
            output?.VerifyCmInvoiceAmountParity?.recommendations ??
            output?.cmInvoicePreCreateParityGate?.recommendations ??
            [],
          mismatches:
            output?.VerifyCmInvoiceAmountParity?.mismatches ??
            output?.cmInvoicePreCreateParityGate?.mismatches ??
            [],
        }
      : output?.cmInvoicePreCreateParityGate?.status === "FAIL"
        ? output.cmInvoicePreCreateParityGate
        : null;
  const subscriptionTransferClassGate = output?.subscriptionTransferClassGate;
  const parentSubscriptionStatusGate = output?.parentSubscriptionStatusGate;
  const billingBoundary = output?.billingBoundaryContext;
  const transferSubtype = transferProfile?.transferSubtype ?? "STANDARD";
  const boundarySource =
    billingBoundary?.boundarySource ??
    transferProfile?.billingBoundarySource ??
    "READY_CHARGES";
  const isPrepaidLastPeriod = transferSubtype === "PREPAID_LAST_PERIOD_ZERO_READY";
  const readyCountAtStart = billingBoundary?.readyChargesCount;
  const boundaryEndDate = billingBoundary?.endDate;
  const profileLabel =
    transferSubtype === "PREPAID_LAST_PERIOD_ZERO_READY"
      ? "Prepaid last period (no READY charges at start)"
      : boundarySource === "READY_CHARGES"
        ? "Standard (unbilled READY charges)"
        : "Standard";
  const boundarySourceLabel =
    boundarySource === "BILLED_CURRENT_PERIOD"
      ? "Current BILLED period (already invoiced)"
      : "Next unbilled period (READY charges)";

  return (
    <Box p={3}>
      {subTransferRequest ? (
        <Paper elevation={3} sx={{ p: 3 }}>
          <Box display="flex" alignItems="center" mb={2} gap={2} flexWrap="wrap">
            <IconButton onClick={() => navigate("/subscription-transfers")} size="small">
              <ArrowBackIcon />
            </IconButton>
            <Typography variant="h4">
              SUBTRANSFER-{subTransferRequest?.requestId}
            </Typography>
            <Chip label={subTransferRequest?.requester} color="default" />
            <StatusChip status={subTransferRequest.status} />
            {transferDateMode === "SAME_DAY" && (
              <Chip label="Same-day transfer" color="info" size="small" variant="outlined" />
            )}
            {transferDateMode === "MULTI_DAY" && (
              <Chip label="Multi-day transfer" color="default" size="small" variant="outlined" />
            )}
            {isPrepaidLastPeriod && (
              <Chip label="Prepaid last period" color="secondary" size="small" variant="outlined" />
            )}
            {isFutureDated && (
              <Chip label="Future-dated" color="warning" size="small" variant="outlined" />
            )}
            {refreshing && <CircularProgress size={24} />}
          </Box>

          {/*
            Context strip: surfaces the operator-facing context every transfer
            has — cutover date, old subscription link, new subscription link,
            and the QC report link if one was written. Kept above the step
            grid so the user can answer "what date?" / "where do I look?" in
            one glance without scrolling to the Input Details accordion.
          */}
          <Box
            display="flex"
            flexWrap="wrap"
            gap={3}
            rowGap={1}
            mb={2}
            sx={{
              backgroundColor: "#f5f7fa",
              borderRadius: 1,
              padding: "12px 16px",
            }}
          >
            {cutoverDate && (
              <Box>
                <Typography variant="caption" color="text.secondary">
                  Cutover date
                </Typography>
                <Typography variant="body2" fontWeight="bold">
                  {cutoverDate}
                </Typography>
              </Box>
            )}
            {oldSubscriptionId && (
              <Box>
                <Typography variant="caption" color="text.secondary">
                  Old subscription
                </Typography>
                <Typography variant="body2">
                  {oldSubscriptionUrl ? (
                    <Link href={oldSubscriptionUrl} target="_blank" rel="noopener noreferrer">
                      {oldSubscriptionId} <OpenInNewIcon sx={{ fontSize: 12 }} />
                    </Link>
                  ) : (
                    oldSubscriptionId
                  )}
                </Typography>
              </Box>
            )}
            {newSubscriptionId && (
              <Box>
                <Typography variant="caption" color="text.secondary">
                  New subscription
                </Typography>
                <Typography variant="body2">
                  {transferIdUrl ? (
                    <Link href={transferIdUrl} target="_blank" rel="noopener noreferrer">
                      {newSubscriptionId} <OpenInNewIcon sx={{ fontSize: 12 }} />
                    </Link>
                  ) : (
                    newSubscriptionId
                  )}
                </Typography>
              </Box>
            )}
            {subTransferRequest.status === "COMPLETED" && output?.completedAt && (
              <Box>
                <Typography variant="caption" color="text.secondary">
                  Completed on
                </Typography>
                <Typography variant="body2" fontWeight="bold">
                  {output.completedAt}
                </Typography>
              </Box>
            )}
            {qcReportHref && (
              <Box>
                <Typography variant="caption" color="text.secondary">
                  Transfer QC report
                </Typography>
                <Typography variant="body2">
                  <Link href={qcReportHref} target="_blank" rel="noopener noreferrer">
                    View transfer QC{" "}
                    <OpenInNewIcon sx={{ fontSize: 12 }} />
                  </Link>
                </Typography>
                {qcReportSheetName && (
                  <Typography
                    variant="caption"
                    color="text.secondary"
                    sx={{ display: "block", lineHeight: 1.2 }}
                  >
                    tab: {qcReportSheetName}
                  </Typography>
                )}
              </Box>
            )}
            {(revenueQcVerdict || revenueQcReportHref) && (
              <Box sx={{ minWidth: 300, maxWidth: 480 }}>
                <Typography variant="caption" color="text.secondary">
                  Revenue QC
                </Typography>
                {revenueQcVerdict && (
                  <Box mb={0.75}>
                    <Chip
                      label={`Overall: ${revenueQcVerdict}`}
                      size="small"
                      color={
                        revenueQcVerdict === "PASS"
                          ? "success"
                          : revenueQcVerdict === "NOT_RUN"
                            ? "default"
                            : "error"
                      }
                      variant={revenueQcVerdict === "NOT_RUN" ? "outlined" : "filled"}
                    />
                  </Box>
                )}
                {output?.revenueQcVerdictGroups?.length ? (
                  <Box mb={0.75}>
                    {output.revenueQcVerdictGroups.map((group) => (
                      <Box key={group.id} sx={{ mb: 1.25 }}>
                        <Box display="flex" alignItems="flex-start" gap={1}>
                          <Typography
                            variant="caption"
                            sx={{ flex: 1, lineHeight: 1.3, pr: 0.5, fontWeight: 600 }}
                          >
                            {group.label}
                          </Typography>
                          <Chip
                            label={formatRevenueQcGroupStatus(group.status)}
                            size="small"
                            color={revenueQcGroupChipColor(group.status)}
                            variant={
                              group.status === "SKIPPED" || group.status === "NOT_RUN"
                                ? "outlined"
                                : "filled"
                            }
                            sx={{ minWidth: 72, flexShrink: 0 }}
                          />
                        </Box>
                        {group.description && (
                          <Typography
                            variant="caption"
                            color="text.secondary"
                            sx={{ display: "block", lineHeight: 1.35, mt: 0.25, fontStyle: "italic" }}
                          >
                            {group.description}
                          </Typography>
                        )}
                        {(group.oldSubscriptionStatus || group.newSubscriptionStatus) && (
                          <Typography
                            variant="caption"
                            color="text.secondary"
                            sx={{ display: "block", lineHeight: 1.35, mt: 0.25 }}
                          >
                            {group.oldSubscriptionStatus
                              ? `Old sub: ${formatRevenueQcGroupStatus(group.oldSubscriptionStatus)}`
                              : null}
                            {group.oldSubscriptionStatus && group.newSubscriptionStatus ? " · " : null}
                            {group.newSubscriptionStatus
                              ? `New sub: ${formatRevenueQcGroupStatus(group.newSubscriptionStatus)}`
                              : null}
                          </Typography>
                        )}
                        {(group.userExplanation ?? group.note) && (
                          <Typography
                            variant="caption"
                            color="text.secondary"
                            sx={{ display: "block", lineHeight: 1.35, mt: 0.25 }}
                          >
                            {group.userExplanation ?? group.note}
                          </Typography>
                        )}
                      </Box>
                    ))}
                  </Box>
                ) : (
                  output?.revenueQcSummary && (
                    <Typography
                      variant="caption"
                      color="text.secondary"
                      sx={{ display: "block", lineHeight: 1.2, mb: 0.75 }}
                    >
                      {output.revenueQcSummary}
                    </Typography>
                  )
                )}
                {revenueQcReportHref && (
                  <Typography variant="body2">
                    <Link href={revenueQcReportHref} target="_blank" rel="noopener noreferrer">
                      View revenue QC{" "}
                      <OpenInNewIcon sx={{ fontSize: 12 }} />
                    </Link>
                  </Typography>
                )}
                {output?.revenueQcReportSheetName && (
                  <Typography
                    variant="caption"
                    color="text.secondary"
                    sx={{ display: "block", lineHeight: 1.2 }}
                  >
                    tab: {output.revenueQcReportSheetName}
                  </Typography>
                )}
              </Box>
            )}
          </Box>

          {subscriptionTransferClassGate &&
            !subscriptionTransferClassGate.allowedToProceed && (
            <Alert severity="error" sx={{ mb: 2 }}>
              <AlertTitle>
                Subscription transfer class gate — transfer stopped before NetSuite changes
              </AlertTitle>
              <Typography variant="body2" sx={{ mb: 1 }}>
                Class: <strong>{subscriptionTransferClassGate.transferClass}</strong>
                {subscriptionTransferClassGate.readyChargesCount != null
                  ? ` · READY=${subscriptionTransferClassGate.readyChargesCount}`
                  : ""}
                {subscriptionTransferClassGate.billedChargesCount != null
                  ? ` · BILLED=${subscriptionTransferClassGate.billedChargesCount}`
                  : ""}
              </Typography>
              <Box component="ul" sx={{ pl: 2, my: 0.5 }}>
                {subscriptionTransferClassGate.gates
                  .filter((g) => g.status === "FAIL")
                  .map((g) => (
                    <Box component="li" key={`class-${g.gateId}`} sx={{ mb: 0.5 }}>
                      <strong>{g.gateId}</strong> ({g.status}): {g.detail}
                    </Box>
                  ))}
              </Box>
              {subscriptionTransferClassGate.combinedError ? (
                <Typography
                  variant="body2"
                  component="div"
                  sx={{
                    mt: 1,
                    whiteSpace: "pre-wrap",
                    wordBreak: "break-word",
                    fontFamily: "monospace",
                    fontSize: "0.8rem",
                  }}
                >
                  {subscriptionTransferClassGate.combinedError}
                </Typography>
              ) : null}
            </Alert>
          )}

          {parentSubscriptionStatusGate && !parentSubscriptionStatusGate.allowedToProceed && (
            <Alert severity="error" sx={{ mb: 2 }}>
              <AlertTitle>
                Parent subscription status gate — transfer stopped before NetSuite changes
              </AlertTitle>
              <Typography variant="body2" sx={{ mb: 1 }}>
                Subscription {parentSubscriptionStatusGate.subscriptionId}: billing status is{" "}
                <strong>
                  {parentSubscriptionStatusGate.statusRefName ||
                    parentSubscriptionStatusGate.statusId ||
                    "(unknown)"}
                </strong>
                {parentSubscriptionStatusGate.statusNormalized
                  ? ` (${parentSubscriptionStatusGate.statusNormalized})`
                  : ""}
                . Past-start transfers require{" "}
                <strong>{parentSubscriptionStatusGate.requiredStatusLabel}</strong>.
              </Typography>
              <Box component="ul" sx={{ pl: 2, my: 0.5 }}>
                {parentSubscriptionStatusGate.gates
                  .filter((g) => g.status === "FAIL")
                  .map((g) => (
                    <Box component="li" key={`parent-${g.gateId}`} sx={{ mb: 0.5 }}>
                      <strong>{g.gateId}</strong> ({g.status}): {g.detail}
                    </Box>
                  ))}
              </Box>
              {parentSubscriptionStatusGate.combinedError ? (
                <Typography
                  variant="body2"
                  component="div"
                  sx={{
                    mt: 1,
                    whiteSpace: "pre-wrap",
                    wordBreak: "break-word",
                    fontFamily: "monospace",
                    fontSize: "0.8rem",
                  }}
                >
                  {parentSubscriptionStatusGate.combinedError}
                </Typography>
              ) : null}
            </Alert>
          )}

          {draftSubscriptionGate && !draftSubscriptionGate.allowedToProceed && (
            <Alert severity="error" sx={{ mb: 2 }}>
              <AlertTitle>Draft subscription gate — transfer stopped before NetSuite changes</AlertTitle>
              <Typography variant="body2" sx={{ mb: 1 }}>
                Parent subscription {draftSubscriptionGate.parentSubscriptionId}:{" "}
                {draftSubscriptionGate.childRows.length === 0
                  ? "no child subscriptions found."
                  : `${draftSubscriptionGate.childRows.length} child(ren) — `}
                {draftSubscriptionGate.childRows
                  .map((c) => `${c.id} (${c.status || c.statusNormalized})`)
                  .join(", ")}
              </Typography>
              <Box component="ul" sx={{ pl: 2, my: 0.5 }}>
                {draftSubscriptionGate.gates
                  .filter((g) => g.status === "FAIL")
                  .map((g) => (
                    <Box component="li" key={g.gateId} sx={{ mb: 0.5 }}>
                      <strong>{g.gateId}</strong> ({g.status}): {g.detail}
                    </Box>
                  ))}
              </Box>
              {draftSubscriptionGate.combinedError ? (
                <Typography
                  variant="body2"
                  component="div"
                  sx={{
                    mt: 1,
                    whiteSpace: "pre-wrap",
                    wordBreak: "break-word",
                    fontFamily: "monospace",
                    fontSize: "0.8rem",
                  }}
                >
                  {draftSubscriptionGate.combinedError}
                </Typography>
              ) : null}
            </Alert>
          )}

          {cmInvoiceParityGate && (
            <Alert severity="error" sx={{ mb: 2 }}>
              <AlertTitle>
                CM / invoice amount mismatch — blocked at Verify CM / Invoice Amount Parity
                {cmInvoiceParityGate.phase === "BEFORE_CREDIT_MEMO"
                  ? " (before credit memo)"
                  : cmInvoiceParityGate.phase === "BEFORE_INVOICE"
                    ? " (before invoice)"
                    : ""}
              </AlertTitle>
              {cmInvoiceParityGate.summary ? (
                <Typography variant="body2" sx={{ mb: 1 }}>
                  {cmInvoiceParityGate.summary}
                </Typography>
              ) : null}
              <Typography variant="body2" sx={{ mb: 1 }}>
                Expected |credit memo|{" "}
                {formatAmount(cmInvoiceParityGate.compareCmAbs, currencyCode) ??
                  (cmInvoiceParityGate.compareCmAbs != null
                    ? Number(cmInvoiceParityGate.compareCmAbs).toFixed(2)
                    : "?")}{" "}
                vs expected invoice{" "}
                {formatAmount(cmInvoiceParityGate.expectedInvoiceSum, currencyCode) ??
                  (cmInvoiceParityGate.expectedInvoiceSum != null
                    ? Number(cmInvoiceParityGate.expectedInvoiceSum).toFixed(2)
                    : "?")}{" "}
                — gap{" "}
                {formatAmount(cmInvoiceParityGate.parityGap, currencyCode) ??
                  (cmInvoiceParityGate.parityGap != null
                    ? Number(cmInvoiceParityGate.parityGap).toFixed(2)
                    : "?")}{" "}
                (tolerance $
                {(cmInvoiceParityGate.tolerance ?? 0.01).toFixed(2)}).
              </Typography>
              <Typography variant="body2" sx={{ mb: 1 }}>
                Fix line sync / proration / negative offsets in NetSuite, then resume this
                request. The Verify CM / Invoice Amount Parity stage re-runs on every resume.
              </Typography>
              {(cmInvoiceParityGate.recommendations?.length ?? 0) > 0 ? (
                <Box component="ul" sx={{ pl: 2, my: 0.5 }}>
                  {cmInvoiceParityGate.recommendations.slice(0, 8).map((rec, idx) => (
                    <Box component="li" key={idx} sx={{ mb: 0.5 }}>
                      {rec}
                    </Box>
                  ))}
                </Box>
              ) : null}
              {(cmInvoiceParityGate.mismatches?.length ?? 0) > 0 ? (
                <Box component="ul" sx={{ pl: 2, my: 0.5 }}>
                  {cmInvoiceParityGate.mismatches.slice(0, 6).map((m, idx) => (
                    <Box component="li" key={`${m.kind}-${idx}`} sx={{ mb: 0.5 }}>
                      <strong>[{m.kind}]</strong> {m.detail}
                    </Box>
                  ))}
                </Box>
              ) : null}
            </Alert>
          )}

          {(transferProfile || billingBoundary) && (
            <Box
              mb={2}
              sx={{
                backgroundColor: "#eef3f8",
                borderRadius: 1,
                padding: "12px 16px",
                border: "1px solid #d0dbe8",
              }}
            >
              <Typography variant="subtitle2" fontWeight="bold" gutterBottom>
                Transfer plan
              </Typography>
              <Box display="flex" flexWrap="wrap" gap={3} rowGap={1}>
                <Box>
                  <Typography variant="caption" color="text.secondary">
                    Profile
                  </Typography>
                  <Typography variant="body2">{profileLabel}</Typography>
                </Box>
                <Box>
                  <Typography variant="caption" color="text.secondary">
                    Ready charges at start
                  </Typography>
                  <Typography variant="body2">
                    {readyCountAtStart != null ? String(readyCountAtStart) : "(not captured yet)"}
                  </Typography>
                </Box>
                <Box>
                  <Typography variant="caption" color="text.secondary">
                    Boundary source
                  </Typography>
                  <Typography variant="body2">{boundarySourceLabel}</Typography>
                </Box>
                {boundaryEndDate && (
                  <Box>
                    <Typography variant="caption" color="text.secondary">
                      Billing boundary (terminate SCO effective)
                    </Typography>
                    <Typography variant="body2" fontWeight="bold">
                      {boundaryEndDate}
                    </Typography>
                  </Box>
                )}
                {billingBoundary?.currentPeriodStart && billingBoundary?.currentPeriodEnd && (
                  <Box>
                    <Typography variant="caption" color="text.secondary">
                      Current billed period
                    </Typography>
                    <Typography variant="body2">
                      {billingBoundary.currentPeriodStart} to {billingBoundary.currentPeriodEnd}
                    </Typography>
                  </Box>
                )}
                <Box>
                  <Typography variant="caption" color="text.secondary">
                    Financial path
                  </Typography>
                  <Typography variant="body2">
                    {transferProfile?.expectedFinancialPath ??
                      transferDateMode ??
                      "(pending)"}
                  </Typography>
                </Box>
                {cutoverDate && (
                  <Box>
                    <Typography variant="caption" color="text.secondary">
                      CM / invoice as-of (cutover)
                    </Typography>
                    <Typography variant="body2">{cutoverDate}</Typography>
                  </Box>
                )}
              </Box>
              {transferProfile?.expectedFinancialPathReason && (
                <Typography variant="caption" color="text.secondary" sx={{ display: "block", mt: 1 }}>
                  {transferProfile.expectedFinancialPathReason}
                </Typography>
              )}
            </Box>
          )}

          {/*
            Conditional banner: future-dated transfers don't terminate the old
            subscription / activate the new one immediately — NetSuite executes
            those moves on the cutover date via the scheduled SCO. Surfacing
            this explicitly stops operators from thinking the transfer "didn't
            do anything" when they see the old subscription still ACTIVE.
          */}
          {isFutureDated && cutoverDate && (
            <Alert severity="info" sx={{ mb: 2 }}>
              <AlertTitle>Cutover is in the future ({cutoverDate})</AlertTitle>
              The old subscription will remain Active and the new subscription
              will remain in Pending Activation until NetSuite executes the
              scheduled subscription change orders on the cutover date.
            </Alert>
          )}

          {/*
            Conditional banner: same-day transfers intentionally skip the
            credit-memo / invoice / payment / journal-entry chain because
            there's nothing to refund or rebill across the boundary. The
            corresponding step cards will show Skipped — this banner explains
            why so it doesn't look like a failure.
          */}
          {transferDateMode === "SAME_DAY" && (
            <Alert severity="info" sx={{ mb: 2 }}>
              <AlertTitle>Same-day transfer — no financial reconciliation needed</AlertTitle>
              The cutover date matches the billing boundary, so no negative offset lines,
              credit memo, invoice, customer payment, journal entry, or customer preference
              changes are required. Those steps are intentionally skipped.
            </Alert>
          )}

          {isPrepaidLastPeriod && transferDateMode === "MULTI_DAY" && (
            <Alert severity="info" sx={{ mb: 2 }}>
              <AlertTitle>Prepaid last period — multi-day financial reconciliation</AlertTitle>
              No READY charges existed on the old subscription at transfer start (last billing
              cycle already invoiced). Cutover falls inside the current billed period
              {billingBoundary?.currentPeriodStart && billingBoundary?.currentPeriodEnd
                ? ` (${billingBoundary.currentPeriodStart} to ${billingBoundary.currentPeriodEnd})`
                : ""}
              . The process credits the unused prepaid portion on the old subsidiary via credit
              memo and bills the same portion on the new subsidiary. Terminate SCO uses the billing
              boundary date{boundaryEndDate ? ` (${boundaryEndDate})` : ""}, not the cutover date.
            </Alert>
          )}

          {transferDateMode === "MULTI_DAY" && !isPrepaidLastPeriod && (
            <Alert severity="info" sx={{ mb: 2 }}>
              <AlertTitle>Multi-day transfer — financial reconciliation will run</AlertTitle>
              Cutover is before the next unbilled billing period
              {boundaryEndDate ? ` (${boundaryEndDate})` : ""}. Negative offset lines, credit memo,
              invoice, customer payment, and intercompany journal entry are expected.
            </Alert>
          )}

          {/*
            Conditional banner: employee field substitutions. Surfaced at the
            top of the page (not inside the step card) so the operator sees
            the action item immediately on page load. Each entry names the
            old employee, the field, and what we did (replaced vs omitted) so
            the operator can decide whether to update the new subscription
            manually or reactivate the original employee on the old data.
          */}
          {employeeSubstitutions.length > 0 && (
            <Alert severity="warning" sx={{ mb: 2 }}>
              <AlertTitle>
                Employee field remediation applied — please verify {employeeSubstitutions.length} field
                {employeeSubstitutions.length === 1 ? "" : "s"} manually
              </AlertTitle>
              <Box component="ul" sx={{ pl: 2, my: 0.5 }}>
                {employeeSubstitutions.map((sub, idx) => (
                  <Box component="li" key={`${sub.field}-${idx}`} sx={{ mb: 0.5 }}>
                    <strong>{sub.fieldLabel}</strong> on the old subscription pointed at
                    employee {sub.oldEmployeeId}{" "}
                    ({sub.reason === "OLD_EMPLOYEE_INACTIVE" ? "inactive" : "rejected by NetSuite"}).{" "}
                    {sub.substitutionOutcome === "REPLACED_WITH_FALLBACK" ? (
                      <>
                        Replaced on the new subscription with{" "}
                        <strong>{sub.fallbackEmployeeName ?? "(unknown)"}</strong>{" "}
                        (id {sub.fallbackEmployeeId ?? "(unknown)"}).
                      </>
                    ) : (
                      <>
                        <strong>Field left unset</strong> — operator must set it manually on the new subscription.
                      </>
                    )}
                  </Box>
                ))}
              </Box>
            </Alert>
          )}

          {/*
            Step grid. Sorted by execution order (`TRANSFER_ORCHESTRATION_STEP_KEYS`) and rendered
            with a 1-based sequence number so the operator can read the
            timeline left-to-right, top-to-bottom in the same order the
            backend orchestrates the work.
          */}
          <Box>
            <Typography variant="h6" mb={1}>Output Details</Typography>
            <Box display="flex" flexWrap="wrap">
              {visibleStepKeys.map((key, idx) => (
                  <Box key={key} width="280px" p={1}>
                    {renderOutputStep(key, idx + 1)}
                  </Box>
                ))}
            </Box>
          </Box>
          {subTransferRequest.status === "FAILED" && (
            <Box
              mb={1}
              p={2}
              borderRadius={2}
              border={1}
              borderColor="error.main"
              bgcolor="#ffdddd"
              color="error.main"
            >
              <Typography variant="body2" sx={{ paddingBottom: "4px" }}>
                <strong>Uh oh!</strong> Sorry that this subscription transfer request has
                failed.
              </Typography>
              {subTransferRequest.output.lastError ? (
                <Typography
                  variant="body2"
                  component="div"
                  sx={{
                    marginTop: 1,
                    whiteSpace: "pre-wrap",
                    wordBreak: "break-word",
                    fontFamily: "monospace",
                    fontSize: "0.8rem",
                  }}
                >
                  {subTransferRequest.output.lastError}
                </Typography>
              ) : null}
              {subTransferRequest.output.failureAt ? (
                <Typography variant="caption" display="block" sx={{ marginTop: 0.5 }}>
                  Recorded at: {subTransferRequest.output.failureAt}
                </Typography>
              ) : null}
            </Box>
          )}
          <InputDetails input={subTransferRequest.input} />
        </Paper>
      ) : (
        <Typography variant="body1">No subscription transfer requests found.</Typography>
      )}
    </Box>
  );
};

export default SubscriptionTransfer;
